import {BillList, SepaRemittance, SepaRemittanceRestControllerApiFactory} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {Alert, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import {Page, PageHeader, Panel, SkeletonRows, SoftChip, StatGrid, StatTile, tableContainerSx, tableSx} from "../../../../components/kit";
import {dateToString} from "../../../../components/utils/dates";
import {REMITTANCE_STATUS} from "./labels";
import {useNavigate, useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import dayjs from "dayjs";
import {CustomNumberFormat} from "../../../../components/utils/custom_number_format";

/**
 * Una remesa SEPA (#12847): sus facturas, el fichero para el banco, presentarla o descartarla, y
 * registrar la devolución de un recibo (#12848). No es un formulario: la remesa no se edita a mano.
 */
export const SepaRemittanceDetailPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(SepaRemittanceRestControllerApiFactory);
	const navigate = useNavigate();
	const {id} = useParams();
	const remittanceId = Number(id);
	const [remittance, setRemittance] = useState<SepaRemittance>();
	const [bills, setBills] = useState<BillList[]>([]);

	const load = () => {
		apiManager.execute(ctrl.getSepaRemittance(remittanceId)).then(setRemittance);
		apiManager.execute(ctrl.billsSepaRemittance(remittanceId)).then((b) => setBills(b as BillList[]));
	};

	useEffect(load, [remittanceId]);

	const downloadXml = () => {
		apiManager.executeRaw(ctrl.xmlSepaRemittance(remittanceId, {responseType: "text"})).then((resp) => {
			const blob = new Blob([resp.data as unknown as string], {type: "application/xml"});
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${remittance?.messageId || "remesa"}.xml`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		});
	};

	const present = () => {
		if (!confirm("¿Marcar la remesa como entregada al banco? Sus facturas pasarán a cobradas el " +
			remittance?.collectionDate + ". Ya no se podrá descartar.")) {
			return;
		}
		apiManager.execute(ctrl.presentSepaRemittance(remittanceId), "Remesa presentada").then(load)
		.catch(() => undefined);
	};

	const discard = () => {
		if (!confirm("¿Descartar este borrador? Sus facturas quedan libres para otra remesa.")) {
			return;
		}
		apiManager.execute(ctrl.discardSepaRemittance(remittanceId), "Remesa descartada")
		.then(() => navigate("/app/admin/billing/sepa-remittance"))
		.catch(() => undefined);
	};

	const registerReturn = (bill: BillList) => {
		const reason = prompt(`Motivo de la devolución de la factura ${bill.number} ` +
			"(el código del banco: AM04 fondos insuficientes, MD06 reembolso…)");
		if (!reason) {
			return;
		}
		const date = prompt("Fecha de la devolución (AAAA-MM-DD)", dayjs().format("YYYY-MM-DD"));
		if (!date) {
			return;
		}
		apiManager.execute(ctrl.registerReturnSepaRemittance(bill.id!, reason, date),
			"Devolución registrada: la factura vuelve a estar sin cobrar").then(load)
		.catch(() => undefined); // el motivo ya lo enseña el aviso de apiManager
	};

	if (!remittance) {
		return <Page><SkeletonRows rows={4}/></Page>;
	}
	const draft = remittance.status === "BORRADOR";
	const st = REMITTANCE_STATUS[remittance.status || ""];

	return (
		<Page>
			<PageHeader back="/app/admin/billing/sepa-remittance"
						title={`Remesa ${remittance.messageId}`}
						subtitle={<>Identificador de acreedor: {remittance.creditorId}</>}
						actions={<>
							{draft && <Button variant={"outlined"} color={"error"} onClick={discard}>Descartar</Button>}
							<Button variant={draft ? "outlined" : "contained"} onClick={downloadXml}>Descargar XML</Button>
							{draft && <Button variant={"contained"} color={"success"} onClick={present}>Marcar presentada</Button>}
						</>}/>

			<StatGrid>
				<StatTile label="Fecha de cargo" value={dateToString(remittance.collectionDate)}/>
				<StatTile label="Estado" value={<SoftChip tone={st?.tone || "neutral"} label={st?.label || remittance.status}/>}
						  hint={remittance.presentedDate &&
							  <Typography variant="caption" color="text.secondary">Presentada el {dateToString(remittance.presentedDate)}</Typography>}/>
				<StatTile label="Facturas" value={remittance.billCount}/>
				<StatTile label="Importe" value={<><CustomNumberFormat amount={remittance.totalAmount}/> €</>}/>
			</StatGrid>

			{draft && (
				<Alert severity={"info"} sx={{mb: 2, borderRadius: 2.5}}>
					Descarga el fichero y súbelo al portal del banco. Cuando el banco lo haya aceptado, márcala
					como presentada: sus facturas pasan a cobradas en la fecha de cargo.
				</Alert>
			)}
			{remittance.notes && (
				<Alert severity={"warning"} sx={{mb: 2, whiteSpace: "pre-line", borderRadius: 2.5}}>{remittance.notes}</Alert>
			)}

			<Panel title="Facturas de la remesa">
				<TableContainer sx={tableContainerSx}>
					<Table size={"small"} sx={{...tableSx, tableLayout: "auto", minWidth: 640}}>
						<TableHead>
							<TableRow>
								<TableCell>Factura</TableCell>
								<TableCell>Cliente</TableCell>
								<TableCell>Vence</TableCell>
								<TableCell align={"right"}>Importe</TableCell>
								<TableCell>Cobro</TableCell>
								<TableCell/>
							</TableRow>
						</TableHead>
						<TableBody>
							{bills.map((b) => (
								<TableRow key={b.id}>
									<TableCell sx={{fontWeight: 600}}>{b.number}</TableCell>
									<TableCell>{b.customer?.shortName || b.customer?.name}</TableCell>
									<TableCell sx={{whiteSpace: "nowrap"}}>{dateToString(b.dueDate)}</TableCell>
									<TableCell align={"right"} sx={{whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums"}}>
										<CustomNumberFormat amount={b.totals?.total}/> €
									</TableCell>
									<TableCell sx={{whiteSpace: "nowrap"}}>
										{b.returnedDate
											? <SoftChip tone="error" label={`Devuelta ${dateToString(b.returnedDate)} · ${b.returnReason || ""}`}/>
											: b.paymentDate ? dateToString(b.paymentDate) : "—"}
									</TableCell>
									<TableCell align={"right"}>
										{!draft && !b.returnedDate && (
											<Button size={"small"} color={"error"} onClick={() => registerReturn(b)}>
												Registrar devolución
											</Button>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Panel>
		</Page>
	);
};
